import { useEffect, useState, useRef } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { storeRequester } from "App";
import InputText from "components/inputs/InputText";
import Select from "react-select";
import CheckCircle from "components/inputs/checkBox/CheckCircle";
import ButtonEclipse from "components/buttons/ButtonEclipse";
import ModalBase from "modals/base/ModalBase";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import style from "./ShippingInfoEditModal.module.css";
import { validateInputs } from "shared/utils/Utils";
import SuperExpressive from "super-expressive";
import { indexOf } from "lodash-es";
import Center from "layouts/wrapper/Center";
import P from "components/P";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { mobileNoFormat } from "InitialData/regExp";
import { toast } from "react-toastify";

const ShippingInfoEditModal = NiceModal.create((props) => {
  const { t } = useTranslation();
  const [withHeader, withFooter] = [true, false];
  const [width, height] = ["min(100%, 800px)", "500px"];
  const withCloseButton = false;
  const clickOutsideToClose = false;
  const title = t("editShippingInfo");
  const buttonText = t("close");

  const modal = useRef();
  const inputs = useRef([]);
  const { callback, setIsAdd } = props; // callback props 추가

  // 폼 데이터 상태 관리
  const [formData, setFormData] = useState({
    name: props.data.name || "",
    province: props.data.province || "",
    firstName: props.data.first_name || "",
    lastName: props.data.last_name || "",
    postalCode: props.data.postal_code || "",
    address1: props.data.address_1 || "",
    address2: props.data.address_2 || "",
    phone: props.data.phone?.replace(/\D/g, "") || "",
    isDefault: props.data?.metadata?.default === true,
  });

  const textFormat = {
    exp: SuperExpressive().startOfInput.caseInsensitive.oneOrMore.anyChar.endOfInput.toRegex(),
    description: "text format",
    sample: "빈칸 없어야 함",
  };

  // 초기 데이터 설정
  useEffect(() => {
    if (props.data) {
      inputs.current[0]?.setValue(props.data.name);
      // inputs.current[0]?.setValue(props.data.province);
      inputs.current[1]?.setValue(props.data.first_name + props.data.last_name);
      inputs.current[2]?.setValue(props.data.postal_code);
      inputs.current[3]?.setValue(props.data.address_1);
      inputs.current[4]?.setValue(props.data.address_2);
      inputs.current[5]?.setValue(props.data.phone?.replace(/\D/g, ""));
      inputs.current[6]?.setValue(props.data?.metadata?.default === true);

    }
  }, [props.data]);

  // 입력값 변경 핸들러
  const handleInputChange = (index, value) => {
    const fieldMap = {
      0: 'name',
      // 0: 'province',
      1: 'firstName',
      2: 'postalCode',
      3: 'address1',
      4: 'address2',
      5: 'phone',
      6: 'isDefault'
    };

    if (inputs.current[index]) {
      inputs.current[index].setValue(value);
    }

    setFormData(prev => ({
      ...prev,
      [fieldMap[index]]: value
    }));
  };

  // 주소 검색 모달
  const addressKrModalClick = () => {
    NiceModal.show("addressKr", { onCallback: addressKrCallback });
  };

  const addressKrCallback = (dataFromModal) => {
    if (dataFromModal) {
      setFormData(prev => ({
        ...prev,
        address1: dataFromModal.address,
        postalCode: dataFromModal.zonecode
      }));

      // ref를 통한 값 설정
      if (inputs.current[2]) inputs.current[2].setValue(dataFromModal.zonecode);
      if (inputs.current[3]) inputs.current[3].setValue(dataFromModal.address);
    }
  };

  // 주소 업데이트 핸들러
  const handleUpdateShippingAddress = async () => {
    const requiredFields = [0, 1, 2, 3, 4,5];
    const isEmpty = requiredFields.some((index) => {
      const value = inputs.current && inputs.current[index]?.getValue()?.toString().trim();
      return !value;
    });
    if (isEmpty) {
      toast.error(t("배송지 정보를 모두 입력해주세요"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }
    const isDefault = inputs.current[6]?.getValue();
    const data = {
      name: formData.name,
      province: formData.province,
      first_name: formData.firstName,
      last_name: formData.lastName,
      postal_code: formData.postalCode,
      address_1: formData.address1,
      address_2: formData.address2,
      city: "city",
      country_code: "kr",
      phone: formData.phone,
      company: "default",
      metadata: { default: isDefault }
    };

    try {
      const result = await storeRequester.updateShippingAddress(props.data.id, data);
      modal.current?.close();
      setTimeout(() => {
        if (callback) {
          callback(result); // updateShippingAddress의 반환값 직접 사용
        }
        setIsAdd?.(prev => !prev);
      }, 100);

    } catch (error) {
      alert("배송지 수정 중 오류가 발생했습니다.");
    }
  };

  return (
    <ModalBase
      ref={modal}
      width={width}
      height={height}
      withHeader={withHeader}
      withFooter={withFooter}
      withCloseButton={withCloseButton}
      clickOutsideToClose={clickOutsideToClose}
      title={title}
      buttonText={buttonText}
      overflow={"visible"}
    >
      <div style={{ padding: "20px" }}>
        <VerticalFlex gap={10}>
          <FlexChild padding={"0 0 10px 0"}>
            <Center width={"100%"} textAlign={"left"}>
              <P size={20} weight={"bold"}>
                {t("shippingInfoAdd")}
              </P>
            </Center>
          </FlexChild>
          <FlexChild>
            <div style={{ height: "1px", backgroundColor: "#dcdcdc" }} />
          </FlexChild>
          
          {/* 배송지명 */}
          <FlexChild>
            <HorizontalFlex gap={10} justifyContent={"flex-start"}>
              <FlexChild width={"20%"}>
                <P weight={"bold"} size={14}>
                  {t("배송지명")}
                </P>
              </FlexChild>
              <FlexChild width={"max-content"} >
                <div style={{ width: "initial" }}>
                  <InputText
                    ref={el => inputs.current[0] = el}
                    defaultValue={formData.name}
                    placeHolder={t("배송지명")}
                    regExp={[textFormat]}
                    onChange={value => handleInputChange(0, value)}
                  />
                </div>
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>

          {/* 이름 */}
          <FlexChild>
            <HorizontalFlex justifyContent={"flex-start"} gap={10}>
              <FlexChild width={"20%"}>
                <P weight={"bold"} size={14}>
                  {t("name")}
                </P>
              </FlexChild>
              <FlexChild width={"39%"}>
                <InputText
                  ref={el => inputs.current[1] = el}
                  defaultValue={formData.firstName}
                  placeHolder={t("name")}
                  regExp={[textFormat]}
                  onChange={value => handleInputChange(1, value)}
                />
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>

          {/* 주소 */}
          <FlexChild>
            <HorizontalFlex justifyContent={"flex-start"} gap={10}>
              <FlexChild width={"20%"}>
                <P weight={"bold"} size={14}>
                  {t("address")}
                </P>
              </FlexChild>
              <FlexChild>
                <VerticalFlex gap={10} flexStart>
                  <FlexChild width={"60%"}>
                    <HorizontalFlex gap={20}>
                      <FlexChild>
                        <InputText
                          ref={el => inputs.current[2] = el}
                          defaultValue={formData.postalCode}
                          readOnly
                          regExp={[textFormat]}
                        />
                      </FlexChild>
                      <FlexChild>
                        <button
                          style={{
                            border: "1px solid lightgray",
                            padding: "5px 10px",
                            borderRadius: "3px",
                          }}
                          onClick={addressKrModalClick}
                        >
                          우편번호 찾기
                        </button>
                      </FlexChild>
                    </HorizontalFlex>
                  </FlexChild>
                  <FlexChild>
                    <HorizontalFlex gap={10}>
                      <FlexChild>
                        <InputText
                          ref={el => inputs.current[3] = el}
                          defaultValue={formData.address1}
                          readOnly
                          regExp={[textFormat]}
                        />
                      </FlexChild>
                      <FlexChild>
                        <InputText
                          ref={el => inputs.current[4] = el}
                          defaultValue={formData.address2}
                          placeHolder={t("상세주소를 입력해주세요 .")}
                          regExp={[textFormat]}
                          onChange={value => handleInputChange(4, value)}
                        />
                      </FlexChild>
                    </HorizontalFlex>
                  </FlexChild>
                </VerticalFlex>
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>

          {/* 전화번호 */}
          <FlexChild>
            <HorizontalFlex justifyContent={"flex-start"} gap={10}>
              <FlexChild width={"20%"}>
                <P weight={"bold"} size={14}>
                  {t("number")}
                </P>
              </FlexChild>
              <FlexChild>
                <HorizontalFlex gap={10} justifyContent={"flex-start"}>
                  <FlexChild width={"49%"}>
                    <InputText
                      ref={el => inputs.current[5] = el}
                      defaultValue={formData.phone}
                      placeHolder={t("enterPhoneNumber")}
                      regExp={[mobileNoFormat]}
                      onChange={value => handleInputChange(5, value)}
                    />
                  </FlexChild>
                </HorizontalFlex>
              </FlexChild>
            </HorizontalFlex>
          </FlexChild>

          <FlexChild>
            <CheckCircle
              ref={(el) => (inputs.current[6] = el)}
              label={t("saveAsDefaultShippingInfo")}
              defaultChecked={formData.isDefault}
            />
          </FlexChild>
        </VerticalFlex>

        {/* 수정 버튼 */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "50px",
          }}
        >
          <button
            onClick={handleUpdateShippingAddress}
            style={{
              backgroundColor: "#296AFF",
              border: "none",
              padding: "10px 20px",
              width: "100%",
              borderRadius: "3px",
              color: "white",
            }}
          >
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>
              주소 수정
            </p>
          </button>
        </div>
      </div>
    </ModalBase>
  );
});

export default ShippingInfoEditModal;
